import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { fetchData, formatNumber, getTranslatedValue, toasterrormsg, toastsuccessmsg } from '../../../../reusable'
import { PageTitle } from '../../../../_metronic/layout/core'
import { KTIcon } from '../../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import DataTable from '../../../components/AppDataTable'
import Loader from '../../component/Loader'
import clsx from 'clsx'
import moment from 'moment'
import Swal from 'sweetalert2'
import { Tr } from '../../../../_metronic/i18n/Tr'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'
import {
  advertisementStatuses,
  adsPauseResumeApiCall,
  formatDuration,
  getStatusBadgeClass,
  pausableStatuses,
  runningStatuses,
} from './AddAdvertisement.constants'

// '' is the "All" tab; the rest map 1:1 to the API status filter
const statusTabs: {display: string; api: string}[] = [
  {display: 'All', api: ''},
  ...advertisementStatuses,
]

const useDebounce = (value: string, delay = 500) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

const ViewAdvertisement: FC = () => {
  const t = Tr()
  const navigate = useNavigate();
  const location = useLocation();
  const lang = useLang();

  var vendorId = ''
  const vendorData = sessionStorage.getItem("vendor")
  if (vendorData) {
    var vendor = JSON.parse(vendorData)
    vendorId = vendor.vendorId
  }

  const [advertisementList, setAdvertisementList] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [focus, setFocus] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [statusTab, setStatusTab] = useState('')

  const debouncedSearch = useDebounce(searchTerm)

  useEffect(() => {
    document.title = 'Insayr | Banner Advertisements'
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const paymentStatusParam = params.get('payment_status')
    const adsId = params.get('transaction_param') || localStorage.getItem('clickPaymentAdsId') || null
    const paymentContextRaw = localStorage.getItem('clickPaymentContext')
    localStorage.removeItem('clickPaymentAdsId')
    localStorage.removeItem('clickPaymentContext')

    if (paymentStatusParam === null) return

    // Click gateway returns payment_status as a number:
    // status < 0 – Error
    // status = 0 – Payment is created
    // status = 1 – Payment is being processed
    // status = 2 – Payment is made successfully
    const clickPaymentStatus = Number(paymentStatusParam)

    // Pass Click's payment_status directly to the API
    if (adsId) {
      const ctx = paymentContextRaw ? JSON.parse(paymentContextRaw) : null
      fetchData('public/addeditadspayment', {
        isPurchase: 1,
        vendorId: ctx?.vendorId ?? Number(vendorId),
        adsId: Number(adsId),
        price: ctx?.price ?? '0',
        paymentMethod: 'click',
        transactionNumber: ctx?.transactionNumber ?? adsId,
        paymentStatus: clickPaymentStatus,
        duration: ctx?.duration ?? '1',
        durationType: ctx?.durationType ?? 'WEEK',
      }).catch((err: any) => {
        console.error('Failed to update payment status', err)
      })
    }

    if (clickPaymentStatus === 2) {
      toastsuccessmsg(t('Payment successful! Your ad has been submitted for review.'))
      navigate(location.pathname, { replace: true })
    } else if (clickPaymentStatus < 0) {
      toasterrormsg(t('Payment failed. Please try again.'))
      navigate('/addadvertisment', {
        replace: true,
        state: { adsId, tab: 4, paymentFailed: true },
      })
    } else if (clickPaymentStatus === 1) {
      toastsuccessmsg(t('Payment is being processed. Your ad will be updated shortly.'))
      navigate(location.pathname, { replace: true })
    } else {
      // status = 0: payment created but not yet confirmed
      navigate(location.pathname, { replace: true })
    }
  }, [])

  // Fetch advertisements list from API
  const fetchAdvertisementsApiCall = async (status: any = '', search: string = '') => {
    setIsLoading(true)
    var param: any = {
      vendorId: String(vendorId)
    }
    if (status !== '') {
      param.status = status
    }
    if (search) {
      param.search = search
    }

    var res = await fetchData('public/getvendorads', param)

    if (res.status == '200') {
      setIsLoading(false)
      setAdvertisementList(res?.data?.data)
    } else {
      setIsLoading(false)
      toasterrormsg(res.message)
    }
  }

  // Delete advertisement API call
  const deleteAdvertisementApiCall = (id: any) => {
    Swal.fire({
      title: t('Are you sure?'),
      text: t('You want to delete this advertisement'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('Yes'),
      cancelButtonText: t('No'),
    }).then(async (result) => {
      if (result.isConfirmed) {
        var param = {
          vendorId: vendorId,
          adsId: id
        }
        var res = await fetchData('public/deleteads', param)

        if (res.status == '200') {
          toastsuccessmsg(t('Advertisement deleted successfully.'))
          fetchAdvertisementsApiCall(filtersRef.current.statusTab, filtersRef.current.search)
        } else {
          toasterrormsg(res.message)
        }
      }
    })
  }

  // The action column is memoised, so its callbacks can hold a stale tab/search.
  // Keep the current filters in a ref so a refetch always uses the visible ones.
  const filtersRef = useRef({ statusTab: '', search: '' })
  filtersRef.current = { statusTab, search: debouncedSearch }

  // Pause an approved ad / resume a paused one
  const pauseResumeAdvertisementApiCall = (id: any, currentStatus: string) => {
    const isPausing = currentStatus !== 'PAUSED'
    Swal.fire({
      title: t('Are you sure?'),
      text: isPausing
        ? t('You want to pause this advertisement')
        : t('You want to resume this advertisement'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('Yes'),
      cancelButtonText: t('No'),
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true)
        var res = await adsPauseResumeApiCall(vendorId, id, isPausing ? 'PAUSED' : 'ACTIVATED')
        setIsLoading(false)

        if (res.status == '200') {
          toastsuccessmsg(
            isPausing
              ? t('Advertisement paused successfully.')
              : t('Advertisement resumed successfully.')
          )
          fetchAdvertisementsApiCall(filtersRef.current.statusTab, filtersRef.current.search)
        } else {
          toasterrormsg(res.message)
        }
      }
    })
  }

  const searchData = (text: any, isfoc: any) => {
    setSearchTerm(text)
    setFocus(isfoc)
  }

  // Single source of truth for the list: any tab / search change refetches
  useEffect(() => {
    fetchAdvertisementsApiCall(statusTab, debouncedSearch)
  }, [statusTab, debouncedSearch])

  const CustomHeader = (props: any) => {
    const inputRef: any = useRef(null)

    const handleChange = async (e: any) => {
      await props.fnserch(e, 1)
    }

    useEffect(() => {
      if (inputRef.current && props.isfoc === 1) {
        inputRef.current.focus()
      }
    }, [props.isfoc])

    return (
      <div className='d-flex justify-content-between flex-wrap w-100 my-5'>
        <div className='d-flex align-items-center position-relative my-1'>
          <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
          <input
            type='text'
            data-kt-user-table-filter='search'
            className='form-control form-control-solid w-250px ps-14'
            placeholder={t('Search Name')}
            value={props.search}
            onChange={(e: any) => handleChange(e.target.value)}
            ref={inputRef}
          />
        </div>
      </div>
    )
  }

  const getStatusDisplay = (status: string) => {
    return advertisementStatuses.find((item) => item.api === status)?.display || status || '-'
  }

  const handleEdit = (row: any) => {
    navigate("/addadvertisment", { state: { adsData: row, adsId: row.adsId } })
  }

  const shouldShowDateColumns = statusTab === '' || runningStatuses.includes(statusTab)

  // Define DataTable columns
  const column: any = useMemo(
    () => {
      const baseColumns = [
        {
          name: <label className='fw-bold fs-6 text-muted'>{t('#')}</label>,
          selector: (row: any, index: any) => index + 1,
          width: '50px',
        },
        {
          name: <label className='fw-bold fs-6 text-muted'>{t('Banner Title')}</label>,
          cell: (row: any) => (
            <div className='my-3'>
              <div className='fw-bold fs-5'>{getTranslatedValue(lang, row, '_title') || row?.en_title || row?.uz_title || row?.ru_title || '-'}</div>
            </div>
          ),
          sortable: false,
          filter: true,
          width: '250px',
        },
        {
          name: <label className='fw-bold fs-6 text-muted'>{t('Status')}</label>,
          cell: (row: any) => (
            <div className='my-3'>
              <span
                className={`btn btn-sm fw-bold w-auto min-w-100px pointer-events-none ${getStatusBadgeClass(
                  row.status
                )}`}
              >
                {t(getStatusDisplay(row.status))}
              </span>
            </div>
          ),
          sortable: false,
          filter: true,
          width: '190px',
        },
        {
          name: <label className='fw-bold fs-6 text-muted'>{t('Placement')}</label>,
          cell: (row: any) => (
            <div className='my-3'>
              <div className='fw-bold fs-5'>{row.placementName || '-'}</div>
            </div>
          ),
          sortable: false,
          filter: true,
          width: '250px',
        },
        {
          name: <label className='fw-bold fs-6 text-muted'>{t('Duration')}</label>,
          cell: (row: any) => (
            <div className='my-3'>
              <div className='fw-bold fs-5'>{formatDuration(row?.duration, row?.durationType, lang)}</div>
            </div>
          ),
          sortable: false,
          filter: true,
          width: '150px',
        },
        {
          name: <label className='fw-bold fs-6 text-muted'>{t('Price per day')}</label>,
          cell: (row: any) => (
            <div className='my-3'>
              <div className='fw-bold fs-5'>{row.perDayPrice != null ? `${formatNumber(row.perDayPrice)} UZS` : '-'}</div>
            </div>
          ),
          sortable: false,
          filter: true,
          width: '150px',
        },
        {
          name: <label className='fw-bold fs-6 text-muted'>{t('Total price')}</label>,
          cell: (row: any) => (
            <div className='my-3'>
              <div className='fw-bold fs-5'>{row.price != null ? `${formatNumber(row.price)} UZS` : '-'}</div>
            </div>
          ),
          sortable: false,
          filter: true,
          width: '150px',
        },
      ]

      const dateColumns = shouldShowDateColumns
        ? [
          {
            name: <label className='fw-bold fs-6 text-muted'>{t('Start Date')}</label>,
            cell: (row: any) => (
              <div className='my-3'>
                <div className='fw-bold fs-5'>
                  {row.startDate ? moment(row.startDate).format('DD.MM.YYYY') : '-'}
                </div>
              </div>
            ),
            sortable: false,
            filter: true,
            width: '150px',
          },
          {
            name: <label className='fw-bold fs-6 text-muted'>{t('End Date')}</label>,
            cell: (row: any) => (
              <div className='my-3'>
                <div className='fw-bold fs-5'>
                  {row.endDate ? moment(row.endDate).format('DD.MM.YYYY') : '-'}
                </div>
              </div>
            ),
            sortable: false,
            filter: true,
            width: '150px',
          },
        ]
        : []

      const createdColumn = {
        name: <label className='fw-bold fs-6 text-muted'>{t('Created')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {row.created ? moment(row.created).format('DD.MM.YYYY') : '-'}
            </div>
          </div>
        ),
        sortable: false,
        filter: true,
        width: '150px',
      }

      const nonEditableStatuses = ['DRAFT', null]

      const actionColumn = {
        name: <label className='fw-bold fs-6 text-muted'>{t('Action')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <button
              className='btn btn-sm btn-icon btn-color-info btn-active-light-info me-2'
              data-toggle='tooltip'
              title={t('View')}
              onClick={() => navigate('/advertisementdetail', { state:  row  })}
            >
              <KTIcon iconName='eye' className='fs-2x text-info' />
            </button>
            {nonEditableStatuses.includes(row.status) && (
              <button
                className='btn btn-sm btn-icon btn-color-primary btn-active-light-primary me-2'
                data-toggle='tooltip'
                title={t('Edit')}
                onClick={() => {
                  handleEdit(row)
                }}
              >
                <KTIcon iconName='pencil' className='fs-2x text-primary' />
              </button>
            )}
            <button
              onClick={() => {
                deleteAdvertisementApiCall(row.adsId)
              }}
              className='btn btn-sm btn-icon btn-color-danger btn-active-light-danger me-2'
              data-toggle='tooltip'
              title={t('Delete')}
            >
              <KTIcon iconName='trash' className='fs-2x text-danger' />
            </button>
            {pausableStatuses.includes(row.status) && (
              <button
                onClick={() => {
                  pauseResumeAdvertisementApiCall(row.adsId, row.status)
                }}
                className={clsx('btn btn-sm fw-bold', {
                  'btn-light-warning': row.status !== 'PAUSED',
                  'btn-light-success': row.status === 'PAUSED',
                })}
                data-toggle='tooltip'
                title={row.status === 'PAUSED' ? t('Resume') : t('Pause')}
              >
                {row.status === 'PAUSED' ? t('Resume') : t('Pause')}
              </button>
            )}
          </div>
        ),
        sortable: false,
        width: '280px',
      }

      return [...baseColumns, ...dateColumns, createdColumn, actionColumn]
    },
    [lang, shouldShowDateColumns, t]
  )

  return (
    <>
      {isLoading && <Loader />}
      <ToastContainer />
      <PageTitle breadcrumbs={[]}>{t('View Advertisements')}</PageTitle>
      {
        <div className='card card-flush py-4'>
          <div className='card-body pt-0'>
            <div className='d-flex justify-content-between align-items-center mb-5'>
              <div className='d-flex align-items-center gap-3 mt-2'>
                <span
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: '#e8f4fd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                    <rect x='2' y='4' width='20' height='14' rx='2' stroke='#009ef7' strokeWidth='1.8' />
                    <path d='M2 9h20' stroke='#009ef7' strokeWidth='1.8' />
                    <path d='M6 14h4M6 17h2' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' />
                  </svg>
                </span>
                <div>
                  <h2 className='mb-0'>{t('Banner Ads')}</h2>
                  <p className='text-muted fs-7 mb-0'>{t('Manage your promotional banner advertisements')}</p>
                </div>
              </div>
              <Link
                to='/addadvertisment'
                state={{ isNew: true }}
                className='btn btn-primary'
              >
                + {t('Create New Ad')}
              </Link>
            </div>

            <div className='card card-custom'>
              <div className='card-header card-header-stretch overflow-auto'>
                <ul
                  className='nav nav-stretch nav-line-tabs fw-semibold border-transparent flex-nowrap'
                  role='tablist'
                >
                  {statusTabs.map((tab) => (
                    <li className='nav-item' key={tab.api || 'ALL'}>
                      <a
                        className={clsx('nav-link cursor-pointer', {
                          active: statusTab === tab.api,
                        })}
                        onClick={() => setStatusTab(tab.api)}
                        role='tab'
                        aria-selected={statusTab === tab.api}
                      >
                        {t(tab.display)}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <DataTable
              columns={column}
              data={advertisementList}
              pagination
              paginationServer={false}
              subHeader={true}
              subHeaderComponent={
                <CustomHeader
                  search={searchTerm}
                  isfoc={focus}
                  fnserch={searchData}
                />
              }
            />
          </div>
        </div>
      }
    </>
  )
}

export default ViewAdvertisement
